import { CurrencyPipe, DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { amountTypeDict, Constants, distributionReasons, positionConstants, RothConversion, RothWarnings, toAccountErrorMap, transferTypes } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Classifications } from '../../../account-info-header/models';
import { selectClassifications, selectMasterAccount, selectSubAccountId } from '../../../account-info-header/reducers';
import * as SelectModels from '../../../shared/controlModels/selectModels';
import { InfoToAccount } from '../../../shared/models/InfoToAccount';
import { FormatDatePipe } from '../../../shared/pipes/FormatDatePipe';
import { RemoveHyphenFromAccountPipe } from '../../../shared/pipes/remove-hyphen-from-account.pipe';
import { selectStandingAuthTableStartTransactionClickedInternalTransfer } from '../../../shared/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { AmountType } from '../../../shared/types/Transaction';
import { RegTypeCode } from "../../../shared/utilities/Constants";
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { mmddyyyyWithSlashes, toAccountAllowedChars } from '../../../shared/utilities/regex-patterns';
import { InternalTransferActions, InternalTransfersActions, PositionsTableActions, TransactionFormActions } from '../../actions';
import { RadioControl } from '../../models/form/FormControlRadio';
import { isInternalTransferForm, ItCashAmounts, ItExtraPrompts, ItToAccountDetails, ItTransactionDetails, TaxWithholdingFields, TransactionForm } from '../../models/form/TransactionForm';
import { DistributionReason } from '../../models/internal-transfer/DistributionReason';
import { InternalTransferInfo } from '../../models/internal-transfer/InternalTransferInfo';
import { ComponentStateById, InternalTransfersInfo, ToAccountInfoById } from '../../models/internal-transfer/InternalTransfersInfo';
import { IraFields } from '../../models/internal-transfer/IraFields';
import { OptionDropdown, SelectControl } from '../../models/OptionDropdownSelect';
import {
  isRestrictedToAccountEntered,
  selectCostBasisMethod,
  selectFrequency,
  selectInputIraFields,
  selectInternalTransferInfo,
  selectInternalTransferServiceError,
  selectInternalTransfersInfo,
  selectInternalTransfersToAccountProperties,
  selectInternalTransferTaxWithholding,
  selectIs401kToAccount,
  selectIsFromEdit,
  selectIsInputandPositionsCallFinished,
  selectIsInputCallFinished,
  selectIsInputCallFinishedMultiple,
  selectIsIraTransaction,
  selectIsRecurring,
  selectITAvailableCashMargin,
  selectITAvailableCashMMF,
  selectITDistributionReason,
  selectITDistributionReasonMap,
  selectItIsRmdEligible,
  selectItTaxPercents,
  selectJournalsUseStandingAuth,
  selectOpenTwePopupConditions,
  selectProcessDate,
  selectPtrsInfo,
  selectTaxWithholdings,
  selectToAccountInfo,
  selectToAccountsInfo,
  selectToAccountsState,
  selectTranasctionFormProcessDate,
  selectTransactionForm,
  selectTransactionFormHasError,
  selectTransferType,
  selectVerifyErrorsByToAccount
} from '../../reducers';
import { currencyStringToNumber } from '../../utilities/currencyStringToNumber';
import { ItMultipleTrustFieldsComponent } from '../it-multiple-trust-fields/it-multiple-trust-fields.component';

@Component({
  standalone: false,
  selector: 'mm-multiple-transaction-details-it',
  templateUrl: './multiple-transaction-details-it.component.html',
  styleUrls: ['./multiple-transaction-details-it.component.scss'],
  providers: [DatePipe, CurrencyPipe]
})
export class MultipleTransactionDetailsItComponent implements OnInit, AfterViewInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() editFormDetails: TransactionForm | undefined;
  @Input() displayPositionsWarning: boolean;
  @Input() displayPositionsWarningPhase2: { displayWarning: boolean, toAccounts: string[], errorMessage: string; };
  @Input() maxLotsWarningMessage: string[];
  @Input() maxLotsWarning: { displayWarning: boolean; };
  @Output() showCashieringIRAAgreementModal = new EventEmitter<boolean>();
  @ViewChild('multipleitTransactionDetails_trustFields') itMultipleTrustFieldsComponent!: ItMultipleTrustFieldsComponent | undefined;
  readonly controlNames = {
    processDate: 'processDate',
    toAccount: 'toAccount'
  };
  isIraTransaction: boolean = false;
  showFromTrustFields: boolean = false;
  toAccountToDelete: string;
  isInputCallFinishedOrEditFlow: boolean = false;
  deleteToAccounts: Subject<void> = new Subject<void>();
  nextBusinessDayCopy: string = Constants.nextBusinessDayCopy;
  recipentError: string = Constants.recipentError;
  registrationType: string;
  itInfo: InternalTransferInfo;
  itInfo$: Observable<InfoToAccount>;
  itsInfo: InternalTransfersInfo;
  toAccountsInfoById: ToAccountInfoById;
  multipleToAccounts: string[] = [];
  toAccountsState$: Observable<ComponentStateById>;
  distributionReason$: Observable<DistributionReason>;
  distributionReasonMap$: Observable<DistributionReason[]>;
  serverError: boolean;
  serverErrorText: string;
  toAccountError: boolean;
  isToAccountInContext: boolean;
  isRecurringTransaction: boolean;
  isToTrustAccount: boolean = false;
  transferType: string;
  showCostBasisMethod: boolean;
  userInput = { toAccount: '', processDate: '', amount: '' };
  hyphenPipe = new RemoveHyphenFromAccountPipe;
  processDateLabel: string = Constants.processDateLabel;
  secondProcessDateLabel: string = Constants.secondProcessDateLabel;
  transactionFormState: TransactionForm;
  endDateLabel: string = Constants.endDateLabel;
  positionProcessDateToolTipMessage: string = positionConstants.PositionProcessDateToolTipMessage;
  subAccountId: string;
  masterAccount: string;
  isFutureYearRmd: boolean;
  currentCostBasis: string = '';
  isOneTimeTotalDist: boolean;
  toAccountControlNames: {
    trustIncomePercent: 'trustIncomeToPercent',
    trustPrincipalPercent: 'trustPrincipalToPercent',
  };
  toAccountAllowedChars = toAccountAllowedChars;
  formatDatePipe = new FormatDatePipe();
  iraFields: IraFields = new IraFields();
  recurringFrequencyModel: SelectControl = {
    title: 'Set frequency of recurring transfer',
    controlName: 'recurringFrequency',
    options: [],
    validationMessage: `Please select 'Frequency' from dropdown.`
  };
  costBasisMethodModel: RadioControl = {
    title: 'Cost basis method',
    displayIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: `When applying a cost basis method, you'll enter a number of shares and Schwab will select lots to transfer per your chosed method. We've preselected this account's default cost basis method for you.`,
    },
    controlName: 'costBasisMethod',
    radios: [
      {
        value: 'FIFO',
        label: ['Account default: FIFO (first-in, first-out)'],
      },
      {
        value: 'LIFO',
        label: ['LIFO (last-in, first-out)'],
      },
      {
        value: 'HCLOT',
        label: ['Highest-cost lot']
      },
      {
        value: 'LCLOT',
        label: ['Lowest-cost lot']
      },
      {
        value: 'None',
        label: [`Select lots manually`]
      }
    ],
    validationMessage: 'Enter a valid Cost Basis Method'
  };
  warningText: string;
  payIncomeModel: SelectControl = SelectModels.payIncomeModel;
  amountTypeSelection: AmountType = amountTypeDict.gross;
  destroy$: Subject<boolean> = new Subject<boolean>();
  useStandingAuth$: Observable<boolean>;
  accountClassifications: Classifications;
  ptrsCallFinished: boolean;
  sourcePtrsFieldsExist: boolean = false;
  distributionReasonList: DistributionReason[] | undefined;
  distributionReason: string;
  availableCashMargin: string;
  availableCashMMF: string;
  taxWithholdings: TaxWithholdingFields;
  autoCalculateRmd$: Observable<boolean>;
  processDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate),
    CustomValidators.notAWeekend
  ];
  recurringProcessDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate),
  ];
  secondProcessDateValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  endDateValidators: ValidatorFn[] = [
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  taxWithholdingFields$: Observable<TaxWithholdingFields>;
  distributionReasonTooltip: string = Constants.itCheckDistributionReasonTooltip;
  downloadRequestFormError: boolean;
  isEdit: boolean;
  toAccountInfo: ItToAccountDetails[] | undefined;
  transactionForm: TransactionForm;
  previousTransferType: string;
  cashAmounts: ItCashAmounts[];
  isUsingStandingAuth: string;
  toAccountsError: string;
  toAccountRegistrationType: string;
  defaultCostBasisMethod: string;
  isI401kToAccount: boolean = false;
  multipleVerifyErrors: string[] = [];

  constructor(private fb: UntypedFormBuilder, private store: Store, public validationService: ValidateInputService, private readonly changeDetectorRef: ChangeDetectorRef, private featureFlagService: FeatureFlagService, private actions$: Actions) {
    this.store.dispatch(
      InternalTransfersActions.itTransactionDetailsComponentLoaded()
    );
  }

  ngAfterViewInit(): void {
    this.checkSourceTrustFields();
  }

  ngOnInit(): void {
    const itInputForm = this.fb.group({
      toAccount: ['', [Validators.required, Validators.minLength(Constants.toAccountInputLength), Validators.maxLength(Constants.toAccountMaxInputLength)]],
      processDate: [''],
    });
    this.toAccountsState$ = this.store.select(selectToAccountsState);

    this.store.select(selectIs401kToAccount).pipe(takeUntil(this.destroy$)).subscribe(is401k => {
      this.isI401kToAccount = is401k;
    });

    this.store.select(selectTransferType).pipe(takeUntil(this.destroy$)).subscribe(type => {
      this.previousTransferType = this.transferType;
      this.transferType = (typeof type === 'string') ? type : "";
    });

    this.parentForm.setControl('transactionDetails', itInputForm);

    this.distributionReason$ = this.store.select(selectITDistributionReason);

    this.distributionReasonMap$ = this.store.select(selectITDistributionReasonMap);

    this.store.select(selectInternalTransferTaxWithholding).pipe(takeUntil(this.destroy$)).subscribe(_taxWithholdings => this.taxWithholdings = _taxWithholdings);

    // Subscribe to transactionForm, when the extra prompts are changed, reset the controls
    this.parentForm.controls.extraPrompts?.valueChanges.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectStandingAuthTableStartTransactionClickedInternalTransfer)),
      withLatestFrom(this.store.select(selectIsFromEdit)),
      filter(([[_, startTransactionClicked], isFromEdit]) => !startTransactionClicked && !isFromEdit)
    ).subscribe(() => {
      this.resetInputFields();
    });

    this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe(classifications => {
      this.accountClassifications = classifications;
    });

    this.store.select(selectPtrsInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.ptrsCallFinished = info.ptrsCallFinished;
    });

    this.store.select(selectITAvailableCashMMF).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.availableCashMMF = info;
    });

    this.store.select(selectITAvailableCashMargin).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.availableCashMargin = info;
    });

    this.store.select(selectTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(val => this.transactionFormState = val);

    // WHEN LOAD TO ACCOUNT HAS ERROR
    combineLatest([
      this.store.select(selectInternalTransfersInfo),
      this.store.select(isRestrictedToAccountEntered)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([state, isrestrictedToAccount]) => {
      if (Object.keys(state.toAccountInfo).length > 0) {
        Object.values(state.toAccountInfo).some(value => {
          this.toAccountRegistrationType = value?.registrationType;
        });
        Object.values(state.toAccountPropertiesById).some(value => {
          this.toAccountsError = value?.loadToAccountErrorText ?? '';
        });
        this.toAccountsError = this.toAccountsError ?? '';
        let is401kToAccount: boolean = this.toAccountRegistrationType === RegTypeCode.ROTH_401K || this.toAccountRegistrationType === RegTypeCode.INDIVIDUAL_401K;

        if (isrestrictedToAccount || (!is401kToAccount && this.toAccountsError != '')) {
          this.downloadRequestFormError = true;
        } else {
          this.downloadRequestFormError = false;
        }
      } else {
        this.downloadRequestFormError = false;
      }
    });

    combineLatest([
      this.store.select(selectInternalTransfersToAccountProperties),
      this.store.select(selectPtrsInfo),
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([propertiesById, ptrsInfo]) => {
      Object.entries(propertiesById).forEach(([key, value]) => {
        if (value.inputIraFields.isIraTransaction && !this.isIraTransaction) {
          this.isIraTransaction = true;
        }
        if (this.isFromPtrsAccount && ptrsInfo.ptrsCallFinished && !this.showFromTrustFields)
          this.showFromTrustFields = true;
      });
    });

    // MAP TAX WITHHOLDING FIELDS
    this.taxWithholdingFields$ = this.store.select(selectIsIraTransaction).pipe(
      filter(isIraTransaction => isIraTransaction),
      withLatestFrom(this.store.select(selectItTaxPercents)),
      map(([_isIraTransaction, taxFields]) => taxFields));

    // SET INPUT FORM FOR POSITIONS
    this.store.select(selectIsInputCallFinishedMultiple).pipe(
      withLatestFrom(
        this.store.select(selectToAccountInfo),
        this.store.select(selectIsFromEdit)
      ),
      takeUntil(this.destroy$)
    ).subscribe(([inputCallFinished, toAccountInfo, isEdit]) => {
      if (this.isPositionsTransaction(this.transferType)) {
        const today: string = this.currentDate();
        this.itInputForm.controls['processDate']?.setValue(today);
        if (this.transferType === transferTypes.POSITIONS) {
          this.itInputForm.removeControl('amount');
        }
        var callPositions = false;
        for (var key in inputCallFinished) {
          callPositions = inputCallFinished[key].inputCallFinished;
        }
        var isOneTimeTotalDistAndCallPositions = this.isOneTimeTotalDist === undefined || this.isOneTimeTotalDist  ? callPositions : true;
        if (isOneTimeTotalDistAndCallPositions && this.itInputForm.controls.toAccounts === undefined && toAccountInfo && toAccountInfo.accountNumber !== '' && !isEdit) {
          this.itInputForm.addControl('toAccounts', new UntypedFormArray([]));
          (this.itInputForm.controls.toAccounts as UntypedFormArray).push(new UntypedFormGroup({
            toAccount: new UntypedFormControl(toAccountInfo.accountNumber),
            ...(toAccountInfo.isTrust ? {
              trustIncomeToPercent: new UntypedFormControl(toAccountInfo.toIncome),
              trustPrincipalToPercent: new UntypedFormControl(toAccountInfo.toPrincipal),
            } : {})
          }));
        }

        // Set isInputCallFinished based on frequency selection
        var itExtraPrompts = this.transactionFormState?.extraPrompts as ItExtraPrompts
        const isUseStandingAuth = itExtraPrompts?.useStandingAuth ?? '';
        const frequency = itExtraPrompts?.frequency ?? '';
        if (isUseStandingAuth === 'yes' && frequency === "onetimeTotalDist" ) {
          if (!this.isInputCallFinishedOrEditFlow) {
          this.isInputCallFinishedOrEditFlow = callPositions;
          }
        } else {
          this.isInputCallFinishedOrEditFlow = true;
        }

        if (callPositions && this.itInputForm?.controls.toAccounts && !(this.serverError || this.serverErrorText)) {
          this.itInputForm?.controls.toAccounts.value.forEach((index: any) => {
            const isDifferentTransferType = this.previousTransferType && this.previousTransferType !== this.transferType;
            if ((isDifferentTransferType && isDifferentTransferType !== undefined) || index.toAccount !== '') {
              this.previousTransferType = this.transferType;
              this.store.dispatch(InternalTransfersActions.itLoadPositions());
              this.itInputForm.addControl('costBasisMethod', new UntypedFormControl('', [Validators.required]));
            }
          });
        }
      } else {
        this.removeCostbaseRadio();
      }
    });

    // SET COST BASIS
    this.store.select(selectIsInputandPositionsCallFinished).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectCostBasisMethod)),
      filter(([inputAndPositionCallComplete, _method]) => inputAndPositionCallComplete)
    ).subscribe(([_inputAndPositionCallComplete, method]) => {
      this.defaultCostBasisMethod = method;
      if (this.isPositionsTransaction(this.transferType) && method != '') {
        if (this.itInputForm.contains('costBasisMethod')) {
          this.showCostBasisMethod = true;
          this.sortCostBasisRadios(method);
          this.currentCostBasis = method;
        }
      } else if (this.isOneTimeTotalDist) {
        this.removeCostbaseRadio();
      }
    });

    // SET COST BASIS FOR EDIT FLOW
    this.store.select(selectIsFromEdit).pipe(takeUntil(this.destroy$)).subscribe(isEdit => {
      if (isEdit && this.editFormDetails && isInternalTransferForm(this.editFormDetails)) {
        this.isInputCallFinishedOrEditFlow = isEdit;
        const transactionDetails = this.editFormDetails?.transactionDetails;
        this.cashAmounts = transactionDetails?.cashAmounts ?? [];
        if (this.isPositionsTransaction(this.transferType)) {
          this.store.select(selectCostBasisMethod).pipe(take(1)).subscribe(defaultCostBasisMethod => {
            var itExtraPrompts = this.editFormDetails?.extraPrompts as ItExtraPrompts;
            if (!(itExtraPrompts?.frequency === "onetimeTotalDist" && (this.editFormDetails?.transactionDetails?.costBasisMethod === undefined))) {
              this.itInputForm.addControl('costBasisMethod', new UntypedFormControl('', [Validators.required]));
            }
            if (defaultCostBasisMethod) {
              this.sortCostBasisRadios(defaultCostBasisMethod);
            }
            this.currentCostBasis = this.editFormDetails?.transactionDetails.costBasisMethod !== undefined && !this.currentCostBasis ? this.editFormDetails.transactionDetails.costBasisMethod : this.currentCostBasis;
            this.costBasisControl?.patchValue(this.currentCostBasis);
            var editCostBasis = this.editFormDetails?.transactionDetails.costBasisMethod !== undefined && this.editFormDetails?.transactionDetails.costBasisMethod !== '' ? this.editFormDetails?.transactionDetails.costBasisMethod : this.currentCostBasis;
            this.costBasisControl.setValue(editCostBasis);
            if (itExtraPrompts.frequency === "onetimeTotalDist" && (this.editFormDetails?.transactionDetails.costBasisMethod === undefined || this.editFormDetails?.transactionDetails.costBasisMethod === '')) {
              this.showCostBasisMethod = false;
            } else {
              this.showCostBasisMethod = true;
            }
            if (this.transferType === transferTypes.POSITIONS) {
              this.itInputForm.removeControl('amount');
            }
          });
          this.store.dispatch(PositionsTableActions.loadEdit());
        }
        if (this.accountClassifications.isPtrs && this.transferType !== transferTypes.POSITIONS) {
          this.transactionDetailsFormGroup.addControl(
            'trustIncomePercent',
            this.fb.control(transactionDetails.trustIncomePercent, [
              Validators.required,
              CustomValidators.zeroOrGreater,
              CustomValidators.lessThanOrEqualTo100,
            ])
          );

          this.transactionDetailsFormGroup.addControl(
            'trustPrincipalPercent',
            this.fb.control(transactionDetails.trustPrincipalPercent, [
              Validators.required,
              CustomValidators.zeroOrGreater,
              CustomValidators.lessThanOrEqualTo100,
            ])
          );

          this.transactionDetailsFormGroup.addControl(
            'trustTaxCategory',
            this.fb.control(transactionDetails.trustTaxCategory, [
              Validators.required
            ])
          );

          this.transactionDetailsFormGroup.addControl(
            'trustTaxCode',
            this.fb.control(transactionDetails.trustTaxCode, [
              Validators.required
            ])
          );

          this.transactionDetailsFormGroup.addControl(
            'trustPaidFor',
            this.fb.control(transactionDetails.trustPaidFor, [
              Validators.required
            ])
          );

          this.transactionDetailsFormGroup.addControl(
            'trustComment',
            this.fb.control(transactionDetails.trustComment)
          );
        }
      }
    });

    // SET IT INFO
    this.itInfo$ = this.store.select(selectInternalTransferInfo).pipe(map(val => {
      let info: InfoToAccount = {
        inputCallFinished: val.inputCallFinished,
        toAccountInfo: val.toAccountInfo
      };
      return info;
    }));
    this.store.select(selectInternalTransferInfo).pipe(takeUntil(this.destroy$)).subscribe(itInfo => {
      this.itInfo = itInfo;
      if (itInfo?.inputCallFinished) {
        this.isToAccountInContext = true;
      }
      if (this.isRecurringTransaction) {
        this.setRecurringFrequencyOptions();
      }
    });

    // SET IRA FIELDS
    this.store.select(selectInputIraFields).pipe(
      takeUntil(this.destroy$)
    ).subscribe(inputIraFields => {
      this.iraFields = inputIraFields;
    });

    //SET TO ACCOUNT FIELDS
    this.store.select(selectToAccountInfo).pipe(
      takeUntil(this.destroy$)
    ).subscribe(toAccountInfo => {
      this.itInputForm?.controls.toAccount.setValue(toAccountInfo.accountNumber);
      this.isToTrustAccount = toAccountInfo.isTrust;
      this.registrationType = toAccountInfo.registrationType;
    });

    this.store.select(selectVerifyErrorsByToAccount).pipe(takeUntil(this.destroy$), distinctUntilChanged((prev, curr) => curr.length === prev.length && curr.every((val, idx) => val === prev[idx]))).subscribe(multipleVerifyErrors => {
      this.multipleVerifyErrors = multipleVerifyErrors;
    });

    // LOAD REVIEW PAGE
    combineLatest([
      this.store.select(selectOpenTwePopupConditions),
      this.store.select(selectTransactionFormHasError),
      this.store.select(selectFrequency),
      this.actions$.pipe(ofType(TransactionFormActions.continueClickTotalDist), startWith(null))
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([popupConditions, formHasError, frequency, continueClickedTotalDist]) => {
      const { verifyCallFinished, openTwePopup } = popupConditions;
      this.isOneTimeTotalDist = frequency === 'onetimeTotalDist';
      if (verifyCallFinished) {
        this.serverError = false;
        this.serverErrorText = '';
      }
      if ((frequency !== 'onetimeTotalDist' && verifyCallFinished && !openTwePopup && !formHasError && this.multipleVerifyErrors.length == 0) || continueClickedTotalDist) {
        this.store.dispatch(TransactionFormActions.loadReviewPage());
      }
    });

    // SET RECURRING FORM FIELDS
    this.store.select(selectIsRecurring).pipe(takeUntil(this.destroy$)).subscribe(isRecurring => {
      this.isRecurringTransaction = isRecurring;
      if (isRecurring) {
        this.itInputForm.addControl('recurringFieldsFormGroup', this.fb.group({
          recurringFrequency: new UntypedFormControl('', [Validators.required])
        }));
        this.setRecurringListeners();
      } else if (this.itInputForm && this.itInputForm.contains('recurringFieldsFormGroup')) {
        this.itInputForm?.removeControl('recurringFieldsFormGroup');
      }
    });

    // SET RMD AMOUNT AND DISTRIBUTION REASON WHEN RMD
    this.store.select(selectIsInputCallFinished).pipe(takeUntil(this.destroy$)).subscribe(inputCallFinished => {
      if (inputCallFinished && this.taxWithholdings) {
        this.taxWithholdings.distributionReason = '';
      }
    });

    // SET PROCESS DATE
    this.store.select(selectProcessDate).pipe(takeUntil(this.destroy$)).subscribe(processDate => {
      if (this.isPositionsTransaction(this.transferType)) {
        this.itInputForm.addControl('processDate', new FormControl(''));
        this.processDate?.setValue(this.formatDatePipe.transform(this.currentDate()));
      }
      else {
        this.processDate?.setValue(this.formatDatePipe.transform(processDate));
      }
    });

    // SERVICE ERROR
    this.store.select(selectInternalTransferServiceError).pipe(takeUntil(this.destroy$)).subscribe(serviceErrorText => {
      let slashIdx = serviceErrorText.lastIndexOf('/');
      let colonIdx = serviceErrorText.lastIndexOf(':');

      // don't show service error for failed verify or input calls - error shown in transaction form
      if (serviceErrorText && !(serviceErrorText.substring(slashIdx + 1, colonIdx) === 'JournalVerify') &&
        !(serviceErrorText.substring(slashIdx + 1, colonIdx) === 'JournalInput')) {
        this.serverError = true;
        this.serverErrorText = serviceErrorText;
      }
    });

    // SET SUBACCOUNT
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(sub => {
      this.subAccountId = sub;
    });

    // SET MASTER ACCOUNT
    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$)).subscribe(masterAccount => {
      this.masterAccount = masterAccount;
    });

    // MAP BOOLEAN OBSERVABLE FOR RECURRING RMD
    this.autoCalculateRmd$ = combineLatest([
      this.store.select(selectIsRecurring),
      this.store.select(selectItIsRmdEligible),
      this.store.select(selectTaxWithholdings),
      this.store.select(selectTranasctionFormProcessDate),
    ]).pipe(map(([isRecurring, isRmdEligible, taxWithholdings, processDate]) => isRecurring && isRmdEligible && taxWithholdings?.distributionReason === distributionReasons.rmd && this.isProcessDateInFutureYear(processDate!)));

    // MAP LOCAL BOOLEAN FOR RECURRING RMD
    this.autoCalculateRmd$.pipe(takeUntil(this.destroy$)).subscribe(isFutureYearRmd => {
      this.isFutureYearRmd = isFutureYearRmd;
    });

    combineLatest([
      this.store.select(selectTransferType),
      this.store.select(selectJournalsUseStandingAuth)
    ]).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectIsFromEdit))
    ).subscribe(([[transferType, isUseStandingAuth], isFromEdit]) => {
      const isDifferentTransferType = this.previousTransferType && this.previousTransferType !== this.transferType;
      if ((isUseStandingAuth !== "yes" && isUseStandingAuth !== "bundled" && isUseStandingAuth !== "bundled-yes") && ((isDifferentTransferType && isDifferentTransferType !== undefined) || this.itInputForm?.controls.toAccount.value === '')) {
        this.previousTransferType = this.transferType;
        this.emitClearToAccounts();
        this.removeCostbaseRadio();
        this.store.dispatch(InternalTransferActions.clearToAccount());
        this.store.dispatch(InternalTransfersActions.itClearToAccounts());
      }
    });
  }

  get transactionDetailsFormGroup() {
    return this.parentForm.controls.transactionDetails as UntypedFormGroup;
  }

  removeCostbaseRadio() {
    this.showCostBasisMethod = false;
    if (this.itInputForm?.contains('costBasisMethod')) {
      this.itInputForm?.removeControl('costBasisMethod');
    }
  }

  currentDate() {
    return DateTime.now().setZone('America/New_York').toFormat('MM/dd/yyyy');
  }

  sortCostBasisRadios(method: string) {
    if (method === "BTAX") {
      this.costBasisMethodModel.radios = [
        {
          value: 'BTAX',
          label: ['Account default: Tax Lot Optimizer'],
          warning: {
            text: positionConstants.taxLotOptimizerInputWarningMessage,
            displayBelow: true
          }
        },
        {
          value: 'FIFO',
          label: ['FIFO (first-in, first-out)'],
        },
        {
          value: 'LIFO',
          label: ['LIFO (last-in, first-out)'],
        },
        {
          value: 'LCLOT',
          label: ['Lowest-cost lot']
        },
        {
          value: 'None',
          label: [`Select lots manually`]
        }
      ];
      let costBasisTaxControlValue = ['FIFO', 'LIFO', 'LCLOT', 'None',];
      for (let i = 0; i < this.costBasisMethodModel.radios.length; i++) {
        if (this.costBasisControl?.value === '') {
          this.costBasisControl?.patchValue(this.costBasisMethodModel.radios[0].value);
        }
        if (costBasisTaxControlValue[i] == this.costBasisMethodModel.radios[i].value) {
          this.costBasisControl?.patchValue(this.costBasisMethodModel.radios[i].value);
        }
      }
    }
    else if (method !== null && method !== '') {
      let costBasisLabel = ['FIFO (first-in, first-out)', 'LIFO (last-in, first-out)', 'Highest-cost lot', 'Lowest-cost lot','Select lots manually'];
      let costBasisValue = ['FIFO', 'LIFO', 'HCLOT', 'LCLOT','None'];
      let costBasisStack: string[] = [];
      let index = 1;
      this.costBasisMethodModel.radios[0].value = method;
      costBasisStack.push(method);
      for (var i = 0; i < costBasisLabel.length; i++) {
        if (costBasisStack.includes(costBasisValue[i]) && method == costBasisValue[i]) {
          this.costBasisMethodModel.radios[0].label = ["Account default: " + costBasisLabel[i]];
          if (this.costBasisControl?.value === '') {
            this.costBasisControl?.patchValue(this.costBasisMethodModel.radios[0].value);
          }
        }
        if (costBasisStack.includes(costBasisValue[i]) && this.costBasisControl?.value == costBasisValue[i]) {
          this.costBasisControl?.patchValue(this.costBasisControl?.value);
        }
        if (!costBasisStack.includes(costBasisValue[i])) {
          this.costBasisMethodModel.radios[index].label = [costBasisLabel[i]];
          this.costBasisMethodModel.radios[index].value = costBasisValue[i];
          index++;
          costBasisStack.push(costBasisValue[i]);
        }
      }
    }

    // Disables select lots manually option for transfers into 401k accounts
    if (this.isI401kToAccount) {
      this.costBasisMethodModel.radios.find(option => option.value === 'None')!.disabled = true;
      this.costBasisMethodModel.radios.find(option => option.value === 'None')!.label = [Constants.selectLotsManually401kDisabled];
      let noneOption = this.costBasisMethodModel.radios.find(option => option.value === 'None');
      if (noneOption && noneOption.disabled) {
        this.costBasisControl?.patchValue(this.costBasisMethodModel.radios[0].value);
      }
    } else {
      this.costBasisMethodModel.radios.find(option => option.value === 'None')!.disabled = false;
      this.costBasisMethodModel.radios.find(option => option.value === 'None')!.label = [`Select lots manually`];
    }

    if (this.transactionFormState?.extraPrompts && 'frequency' in this.transactionFormState.extraPrompts && this.transactionFormState.extraPrompts.frequency === 'onetimeTotalDist') {
      this.disableCostBasisMethodRadios();
    };
  }

  toAccounttoDeleteEvent(params: any) {
    this.toAccountToDelete = params;
  }

  setRecurringListeners(): void {
    this.recurringFrequency?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value === 'SEMIMTH') {
        this.recurringFieldsFormGroup?.removeControl('payIncome');
        if (this.isFutureYearRmd) {
          this.amount?.setValue(Constants.zeroAmount);
        } else {
          this.amount?.enable();
          this.amount?.markAsUntouched();
        }
      } else if (value === 'LASTBIZ') {
        this.recurringFieldsFormGroup?.addControl('payIncome', new UntypedFormControl(''));
        this.recurringFieldsFormGroup?.removeControl('secondProcessDate');
        this.payIncome?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
          if (value) {
            this.amount?.setValue('');
            this.amount?.disable();
            this.amountType?.disable();
          } else if (this.isFutureYearRmd) {
            this.amount?.setValue(Constants.zeroAmount);
          } else {
            this.amount?.enable();
            this.amountType?.enable();
            this.amount?.markAsUntouched();
          }
        });
      } else {
        this.recurringFieldsFormGroup?.removeControl('secondProcessDate');
        this.recurringFieldsFormGroup?.removeControl('payIncome');
        if (this.isFutureYearRmd) {
          this.amount?.setValue(Constants.zeroAmount);
        } else {
          this.amount?.enable();
          this.amount?.markAsUntouched();
        }
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  onProcessDateChange(date: string) {
    this.processDate?.setValue(date);
  }

  onEndDateChange(date: string) {
    this.endDate?.setValue(date);
  }

  onSecondProcessDateChange(date: string) {
    this.secondProcessDate?.setValue(date);
  }

  checkSourceTrustFields(): void {
    this.itMultipleTrustFieldsComponent ? this.sourcePtrsFieldsExist = true : this.sourcePtrsFieldsExist = false;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  resetInputFields(): void {
    this.amount?.reset();
    this.processDate?.reset();
    this.itInputForm.get('recurringFieldsFormGroup')?.reset();
    this.isToAccountInContext = false;
    this.serverError = false;
    this.serverErrorText = '';
    this.iraFields = new IraFields();
    var account = this.toAccountToDelete;
    if (account != '') {
      this.store.dispatch(InternalTransfersActions.itClearToAccount({ account }));
      this.toAccountToDelete = '';
    }

    this.store.select(selectToAccountsInfo).pipe(
      takeUntil(this.destroy$),
      map(toAccountsList => Object.keys(toAccountsList).length === 0)
    ).subscribe(value => {
      if (value) {
        this.removeCostbaseRadio();
      }
    });
  }
  emitClearToAccounts() {
    this.deleteToAccounts.next();
  }
  resetForm() {
    this.resetInputFields();
    this.sortCostBasisRadios(this.defaultCostBasisMethod);
  }

  isRothConversionRequest() {
    return this.itInfo.inputIraFields.distributionReasonMap.some(
      d =>
        (d.code.trim() === RothConversion.DistributionReasons.OVER_59_AND_A_HALF_CONVERSION_CODE ||
          d.code.trim() === RothConversion.DistributionReasons.UNDER_59_AND_A_HALF_CONVERSION_CODE) &&
        d.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE
    );
  }

  isPositionsTransaction(transferType?: string): boolean {
    return transferType === transferTypes.POSITIONS || transferType === transferTypes.POSITIONS_CASH;
  }
  get toAccount() {
    return this.itInputForm.get('toAccount');
  }

  get processDate() {
    return this.itInputForm?.get('processDate');
  }

  get isFromPtrsAccount() {
    return this.accountClassifications.isPtrs ? true : false;
  }

  get amount() {
    return this.itInputForm.get('amount');
  }

  get amountType() {
    return this.itInputForm.get('amountType');
  }

  get disableDateSelect() {
    return (this.accountClassifications.isIra && this.isRothConversionRequest()) || this.isPositionsTransaction(this.transferType);
  }

  get recurringFrequency() {
    return this.recurringFieldsFormGroup?.get('recurringFrequency');
  }

  get endDate() {
    return this.recurringFieldsFormGroup?.get('endDate');
  }

  get secondProcessDate() {
    return this.recurringFieldsFormGroup?.get('secondProcessDate');
  }

  get payIncome() {
    return this.recurringFieldsFormGroup?.get('payIncome');
  }

  get itInputForm(): UntypedFormGroup {
    return this.parentForm.controls?.transactionDetails as UntypedFormGroup;
  }

  get taxWithholdingsFormGroupDetails() {
    return (this.editFormDetails?.transactionDetails as ItTransactionDetails)?.taxWithholdingsFormGroup;
  }

  get recurringFieldsFormGroup(): UntypedFormGroup {
    return this.itInputForm.controls?.recurringFieldsFormGroup as UntypedFormGroup;
  }

  get extraPrompts(): UntypedFormGroup {
    return this.parentForm.controls.extraPrompts as UntypedFormGroup;
  }

  get showTrustFields(): boolean {
    if (this.isRecurringTransaction || !this.isToAccountInContext || (!this.itInfo.inputCallFinished && !this.editFormDetails)) {
      return false;
    }

    if (this.ptrsCallFinished || (this.isToTrustAccount && !this.isFromPtrsAccount && this.isToAccountInContext && !this.itInfo.isTrustNonPTRSAccount)) {
      return true;
    }
    return false;
  }

  get toAccountValidationError() {
    if (this.toAccount && this.toAccount.errors) {
      const errorKeys: string[] = Object.keys(this.toAccount.errors ?? {});
      if (errorKeys.length > 0) {
        return toAccountErrorMap[errorKeys[0] as keyof typeof toAccountErrorMap];
      }
    }
  }

  get availableCash(): number {
    return currencyStringToNumber(this.availableCashMargin) + currencyStringToNumber(this.availableCashMMF);
  }

  get costBasisControl(): UntypedFormControl {
    return this.itInputForm?.get('costBasisMethod') as UntypedFormControl;
  }

  get overdraftRulesFutureDate(): Date {
    let today = new Date(new Date().setHours(0, 0, 0, 0));
    return new Date(today.setDate(today.getDate() + 1));
  }

  get futureDate(): boolean {
    if (this.processDate !== null) {
      var selectedDate = new Date(this.processDate!.value);
      return selectedDate.setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);
    }
    return false;
  }

  get showServerError() {
    this.registrationType = this.registrationType != undefined && this.registrationType != '' ? this.registrationType : this.toAccountRegistrationType;
    const isI401k = this.registrationType === RegTypeCode.ROTH_401K || this.registrationType === RegTypeCode.INDIVIDUAL_401K;
    return this.serverError && (!isI401k || this.serverErrorText != this.recipentError);
  }

  isProcessDateInFutureYear(date: string): boolean {
    if (date) {
      let curYear = new Date().getFullYear();
      let yearSelected = new Date(date).getFullYear();
      return yearSelected > curYear;
    }
    return false;
  }

  get showWarning() {
    if (!this.itInfo.inputCallFinished && !this.editFormDetails) {
      return false;
    }

    let distributionReasonList = this.itInfo.inputIraFields.distributionReasonMap;
    let distributionReason = this.itInfo.inputIraFields.distributionReason;

    let selectedReason = distributionReasonList?.filter(
      (element) =>
        element?.description === distributionReason?.description
    );

    let under59SelectionPossible = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE &&
        element.code.trim() ===
        RothConversion.DistributionReasons.UNDER_59_AND_A_HALF_CONVERSION_CODE
    );
    let over59SelectionPossible = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE &&
        element.code.trim() ===
        RothConversion.DistributionReasons.OVER_59_AND_A_HALF_CONVERSION_CODE
    );
    let CombineAccountPossible = selectedReason?.some(
      (element) =>
        element.subCode === RothConversion.DistributionReasons.COMBINATION_SUB_CODE &&
        element.code.trim() ===
        RothConversion.DistributionReasons.COMBINATION_CONVERSION_CODE
    );

    let isQualifiedDistribution = selectedReason?.some(
      (element) =>
        element.code.trim() ===
        RothConversion.DistributionReasons.QUALIFIED_CODE
    );

    if (under59SelectionPossible && !this.accountClassifications.isCustomer59OrOlder) {
      this.warningText = RothWarnings.under59InputWarning;
      return true;
    } else if (over59SelectionPossible) {
      this.warningText = RothWarnings.over59InputWarning;
      return true;
    } else if (CombineAccountPossible) {
      this.warningText = RothWarnings.Comb0InputWarning;
      return true;
    } else if (isQualifiedDistribution) {
      this.warningText = RothWarnings.QualifiedDistributionWarning;
      return true;
    } else {
      return false;
    }
  }

  setRecurringFrequencyOptions(): void {
    this.recurringFrequencyModel.options =
      this.itInfo.recurringFields.frequencyList
        .filter(
          (frequency) =>
            frequency.description !== 'Select One' &&
            frequency.code !== 'ACCRUED'
        )
        .map(
          (frequency) =>
            new OptionDropdown(frequency.code, frequency.description)
        );
  }

  isInvalidDigitalToPdfToAccount(): boolean {
    return (this.registrationType == RegTypeCode.INDIVIDUAL_401K || this.registrationType == RegTypeCode.ROTH_401K);
  }

  get minDate(): Date {
    return new Date();
  }

  get maxDate(): Date {
    let maxDate: Date = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    return maxDate;
  }

  get displayTaxWithholdingsFields() {
    return this.iraFields.isIraTransaction && (this.itInfo.inputCallFinished || this.editFormDetails);
  }

  displayIraAgreementModal() {
    this.showCashieringIRAAgreementModal.emit(true);
  }

  disableCostBasisMethodRadios() {
    this.costBasisMethodModel.radios.forEach(radio => {
      radio.disabled = true;
    });
  }

  scrollTo(elemName: string){
    setTimeout(() => {
      const bannerEl = document.getElementById(elemName);
      bannerEl?.focus();
      bannerEl?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    }, 100)
  }
}
